import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import AdminShell from "@/components/admin/AdminShell";
import BookingForm from "@/components/admin/BookingForm";
import PaymentRequestPanel from "@/components/admin/PaymentRequestPanel";
import { PaymentStatusBadge } from "@/components/admin/StatusBadge";
import {
  BookingWithClient,
  bookingToForm,
  getBooking,
  listGroupBookings,
  setBookingStatus,
  updateBooking,
} from "@/lib/bookingsApi";
import { sendPaymentRequest } from "@/lib/emailApi";
import { daysUntil, formatDisplayDate, formatTime, isOverdue } from "@/lib/bookingLabels";
import MapsButton from "@/components/admin/MapsButton";
import GroupPanel from "@/components/admin/GroupPanel";
import { MarkPaidButton, WhatsAppButton } from "@/components/admin/QuickActions";
import { Copy } from "lucide-react";
import { formatGbpFromPence, type ReminderHistory } from "@/types/database";
import { isSupabaseConfigured, siteUrl, supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

function timeRange(start: string | null, end: string | null) {
  const s = formatTime(start);
  const e = end ? formatTime(end) : "";
  if (s && e) return `${s}–${e}`;
  return s || "—";
}

const BookingDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<BookingWithClient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [reminders, setReminders] = useState<ReminderHistory[]>([]);
  const autoPreview = searchParams.get("send") === "1";
  const [group, setGroup] = useState<{ token: string; rows: BookingWithClient[] } | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const res = await getBooking(id);
    if (res.ok === false) {
      setError(res.error);
      setBooking(null);
    } else {
      setError(null);
      setBooking(res.data);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!booking?.group_id) { setGroup(null); return; }
    void listGroupBookings(booking.group_id).then((r) => {
      if (r.ok) setGroup({ token: r.data.pay_token, rows: r.data.bookings });
    });
  }, [booking?.group_id, booking?.payment_status, message]);

  useEffect(() => {
    if (booking) document.title = `${booking.reference} · Admin`;
  }, [booking]);

  useEffect(() => {
    if (!id || !supabase || !isSupabaseConfigured) return;
    void supabase
      .from("reminder_history")
      .select("*")
      .eq("booking_id", id)
      .order("run_at", { ascending: false })
      .then(({ data }) => setReminders((data as ReminderHistory[]) ?? []));
  }, [id, message]);

  // Stable form values: only rebuild when the saved booking itself changes,
  // otherwise background re-renders would wipe edits in progress.
  const formInitial = useMemo(() => (booking ? bookingToForm(booking) : null), [booking]);

  const live = (group?.rows ?? []).filter((r) => r.booking_status !== "cancelled" && r.booking_status !== "archived");
  const isBlock = live.length > 1;
  const blockOwed = live.filter((r) => r.payment_status !== "paid" && r.payment_status !== "not_required").reduce((s, r) => s + r.fee_pence, 0);
  const payLink = booking
    ? isBlock && group
      ? `${siteUrl || window.location.origin}/booking/pay-group/${group.token}`
      : `${siteUrl || window.location.origin}/booking/pay/${booking.pay_token}`
    : "";
  const groupMsg = isBlock ? { link: payLink, totalPence: blockOwed, count: live.length } : undefined;

  useEffect(() => {
    if (autoPreview) {
      // Clear ?send=1 so refresh doesn't re-open forever
      const next = new URLSearchParams(searchParams);
      next.delete("send");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPreview]);

  const onStatus = async (status: "cancelled" | "archived" | "scheduled") => {
    if (!id) return;
    if (status === "cancelled" && !window.confirm("Cancel this booking?")) return;
    const res = await setBookingStatus(id, status, {
      cancel_reason: status === "cancelled" ? "Cancelled by admin" : undefined,
    });
    if (res.ok === false) {
      setError(res.error);
      return;
    }
    setMessage(
      status === "cancelled"
        ? "Booking cancelled."
        : status === "archived"
          ? "Booking archived."
          : "Booking restored to scheduled."
    );
    await load();
    setEditing(false);
  };

  if (loading) {
    return (
      <AdminShell title="Booking">
        <p className="text-white/50 text-sm">Loading…</p>
      </AdminShell>
    );
  }

  if (!booking) {
    return (
      <AdminShell title="Booking">
        <p className="text-red-300 text-sm mb-4">{error ?? "Not found"}</p>
        <Link to="/admin/bookings" className="text-primary text-sm hover:underline">
          Back to bookings
        </Link>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      title={booking.reference}
      actions={
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            className="rounded-full border border-white/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-primary hover:text-primary"
          >
            {editing ? "View" : "Edit / Reschedule"}
          </button>
          <Link
            to="/admin/bookings"
            className="rounded-full border border-white/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-primary hover:text-primary"
          >
            All bookings
          </Link>
        </div>
      }
    >
      {message && (
        <p className="mb-4 text-sm text-primary font-inter" role="status">
          {message}
        </p>
      )}
      {error && (
        <p className="mb-4 text-sm text-red-300 font-inter" role="alert">
          {error}
        </p>
      )}

      {!editing ? (
        <div className="space-y-6">
          {booking.group_id && <GroupPanel groupId={booking.group_id} currentId={booking.id} />}
          <div className="admin-card p-5 md:p-6">
            {isBlock && <p className="text-xs uppercase tracking-widest text-primary/80 font-inter mb-3">This performance · {booking.reference}</p>}
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="font-playfair text-2xl">
                  {booking.clients?.venue_or_client_name}
                </h2>
                <p className="text-white/50 text-sm font-inter mt-1">
                  {formatDisplayDate(booking.performance_date)} ·{" "}
                  {formatTime(booking.start_time)}
                  {booking.end_time ? `–${formatTime(booking.end_time)}` : ""}
                </p>
              </div>
              <PaymentStatusBadge
                paymentStatus={booking.payment_status}
                bookingStatus={booking.booking_status}
                dueDate={booking.due_date}
              />
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm font-inter">
              <div>
                <dt className="text-white/40 text-xs uppercase tracking-wider mb-1">{isBlock ? "This performance" : "Total to pay"}</dt>
                <dd className="text-primary text-lg">{formatGbpFromPence(booking.fee_pence)}</dd>
                {(booking.fuel_charge_pence ?? 0) > 0 && (
                  <dd className="text-white/45 text-xs mt-0.5">
                    Includes {formatGbpFromPence(booking.fuel_charge_pence ?? 0)} fuel
                    {booking.distance_miles ? ` (${booking.distance_miles} miles there and back)` : ""}
                  </dd>
                )}
              </div>
              <div>
                <dt className="text-white/40 text-xs uppercase tracking-wider mb-1">Pay by</dt>
                <dd className={isOverdue(booking.payment_status, booking.due_date) ? "text-destructive font-medium" : ""}>
                  {booking.due_date ? formatDisplayDate(booking.due_date) : "—"}
                  {isOverdue(booking.payment_status, booking.due_date) && booking.due_date ? ` · overdue ${Math.abs(daysUntil(booking.due_date))} days` : ""}
                </dd>
              </div>
              <div>
                <dt className="text-white/40 text-xs uppercase tracking-wider mb-1">Contact</dt>
                <dd>
                  {booking.clients?.contact_name ?? "—"}
                  <br />
                  <span className="text-white/50">{booking.clients?.email ?? ""}</span>
                  <br />
                  <span className="text-white/50">{booking.clients?.phone ?? ""}</span>
                </dd>
              </div>
              <div>
                <dt className="text-white/40 text-xs uppercase tracking-wider mb-1">Address</dt>
                <dd className="text-white/80">
                  {booking.address ?? "—"}
                  {booking.venue_postcode ? (
                    <>
                      <br />
                      <span className="text-white/60">{booking.venue_postcode}</span>
                    </>
                  ) : null}
                </dd>
                {(booking.address || booking.venue_postcode || booking.clients?.venue_or_client_name) && (
                  <div className="mt-3">
                    <MapsButton
                      destination={
                        [booking.address, booking.venue_postcode].filter(Boolean).join(", ") ||
                        booking.clients?.venue_or_client_name ||
                        ""
                      }
                      prominent={daysUntil(booking.performance_date) === 0}
                    />
                  </div>
                )}
              </div>
              {booking.internal_notes && (
                <div className="sm:col-span-2">
                  <dt className="text-white/40 text-xs uppercase tracking-wider mb-1">
                    Internal notes
                  </dt>
                  <dd className="text-white/70 whitespace-pre-wrap">{booking.internal_notes}</dd>
                </div>
              )}
            </dl>
          </div>

          <div className="flex flex-wrap gap-2">
            <MarkPaidButton booking={booking} onDone={() => { setMessage("Marked as paid."); void load(); }} />
            {booking.payment_status !== "paid" && <WhatsAppButton booking={booking} group={groupMsg} chase={!!booking.due_date && daysUntil(booking.due_date) < 0} />}
            <button
              type="button"
              onClick={() => navigate(`/admin/bookings/new?from=${booking.id}`)}
              className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-inter border border-white/20 hover:border-primary min-h-[40px]"
            >
              <Copy className="w-4 h-4" /> Copy booking
            </button>
          </div>

          <PaymentRequestPanel
            bookingId={booking.id}
            payLink={payLink}
            clientEmail={booking.clients?.email}
            autoPreview={autoPreview}
            onMessage={setMessage}
            booking={{
              reference: booking.reference,
              venueName: booking.clients?.venue_or_client_name ?? "",
              contactName: booking.clients?.contact_name ?? "",
              email: booking.clients?.email ?? "",
              performanceDate: isBlock ? `${live.length} performances` : formatDisplayDate(booking.performance_date),
              performanceTime: isBlock ? "See your booking page for each date and time" : timeRange(booking.start_time, booking.end_time),
              address: [booking.address, booking.venue_postcode].filter(Boolean).join(", "),
              feePence: isBlock ? blockOwed : booking.fee_pence,
              dueDate: booking.due_date ? formatDisplayDate(booking.due_date) : "",
              description: booking.client_description ?? "",
            }}
          />

          <details className="admin-card p-5 md:p-6 group">
            <summary className="cursor-pointer list-none font-inter text-sm text-white/60 flex justify-between">Reminder history <span className="text-white/35 group-open:hidden">Show</span></summary>
            <div className="mt-3">
            {reminders.length === 0 ? (
              <p className="text-sm text-white/40 font-inter">No automated reminders yet.</p>
            ) : (
              <ul className="space-y-2">
                {reminders.map((r) => (
                  <li
                    key={r.id}
                    className="text-xs font-inter text-white/65 flex flex-col sm:flex-row sm:justify-between gap-1 border border-white/10 rounded-xl px-3 py-2"
                  >
                    <span>
                      {r.kind} · {r.status}
                      {r.details ? ` — ${r.details}` : ""}
                    </span>
                    <span className="text-white/35">
                      {new Date(r.run_at).toLocaleString("en-GB", { timeZone: "Europe/London" })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            </div>
          </details>

          <div className="flex flex-wrap gap-2">
            {booking.booking_status !== "cancelled" && (
              <button
                type="button"
                onClick={() => onStatus("cancelled")}
                className="rounded-full border border-red-400/40 text-red-300 px-5 py-2.5 text-xs uppercase tracking-widest"
              >
                Cancel
              </button>
            )}
            {booking.booking_status === "cancelled" && (
              <button
                type="button"
                onClick={() => onStatus("scheduled")}
                className="rounded-full border border-white/20 px-5 py-2.5 text-xs uppercase tracking-widest"
              >
                Restore
              </button>
            )}
            {booking.booking_status === "cancelled" && (
              <button
                type="button"
                onClick={async () => {
                  if (!supabase || !window.confirm("Delete this booking for good? This cannot be undone.")) return;
                  const bid = booking.id;
                  await supabase.from("reminder_history").delete().eq("booking_id", bid);
                  await supabase.from("email_history").delete().eq("booking_id", bid);
                  await supabase.from("payments").delete().eq("booking_id", bid);
                  const { error: dErr } = await supabase.from("bookings").delete().eq("id", bid);
                  if (dErr) { setError(dErr.message); return; }
                  navigate("/admin/bookings");
                }}
                className="rounded-full bg-destructive text-destructive-foreground px-5 py-2.5 text-xs uppercase tracking-widest"
              >
                Delete for good
              </button>
            )}
            {booking.booking_status !== "archived" && (
              <button
                type="button"
                onClick={() => onStatus("archived")}
                className="rounded-full border border-white/20 px-5 py-2.5 text-xs uppercase tracking-widest"
              >
                Archive
              </button>
            )}
          </div>
        </div>
      ) : (
        <BookingForm
          mode="edit"
          initial={formInitial ?? bookingToForm(booking)}
          excludeBookingId={booking.id}
          saving={saving}
          onSave={async (values) => {
            setSaving(true);
            const res = await updateBooking(booking.id, values, user?.id);
            setSaving(false);
            if (res.ok === false) throw new Error(res.error);
            setBooking(res.data);
            setEditing(false);
            setMessage("Booking updated.");
          }}
          onSaveAndSend={async (values) => {
            setSaving(true);
            const res = await updateBooking(booking.id, values, user?.id);
            setSaving(false);
            if (res.ok === false) throw new Error(res.error);
            setBooking(res.data);
            setEditing(false);
            const sendRes = await sendPaymentRequest(res.data.id);
            if (sendRes.ok === false) {
              setMessage(
                `Booking saved. Email not sent yet: ${sendRes.error} Use Preview / Send below when ready.`
              );
            } else {
              setMessage(`Booking saved and payment request emailed to ${sendRes.data.to_email}.`);
            }
          }}
        />
      )}
    </AdminShell>
  );
};

export default BookingDetail;
